import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Image, { type StaticImageData } from 'next/image';
import { forwardRef, useState } from 'react';

import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import type { IntegrationVendor } from '@/components/integrations/types';
import { IntegrationVendorDetails } from '@/components/integrations/types';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { useEnvironment } from '@/contexts/useEnvironment';

export const ShareResultsButton = () => {
  const { isDemo, isDev } = useEnvironment();
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    IntegrationVendor | undefined
  >();

  if (!(isDemo || isDev)) {
    return null;
  }

  const selectedIntegration =
    selectedIntegrationId && IntegrationVendorDetails[selectedIntegrationId];

  return (
    <>
      <OptionMenu
        placement='left'
        Component={forwardRef<HTMLButtonElement>((props, ref) => (
          <Tooltip title='Share data to...'>
            <IconButton
              disableRipple
              size='small'
              data-testid='option-menu-button'
              ref={ref}
              {...props}
            >
              <i
                className={
                  'material-symbols-share-outline text-(--mui-palette-text-primary)'
                }
              />
            </IconButton>
          </Tooltip>
        ))}
        onOptionSelected={(option) => {
          if (typeof option !== 'string') {
            setSelectedIntegrationId(option.id as IntegrationVendor);
          }
        }}
        options={Object.entries(IntegrationVendorDetails).map(
          ([key, value]) => ({
            id: key,
            text: value.displayName,
            icon: <Logo alt={value.displayName} src={value.logo} />,
          })
        )}
      />
      {selectedIntegration && (
        <ConfirmationDialog
          isOpen={true}
          title={
            <Stack alignItems='center' gap='24px'>
              <Image
                alt={selectedIntegration.displayName}
                src={selectedIntegration.logo}
                width={24}
                height={24}
                style={{ width: '24px', height: '24px' }}
              />
              <Typography variant='h2'>
                {`Share data to ${selectedIntegration.displayName}`}
              </Typography>
            </Stack>
          }
          description={`Eon grants ${selectedIntegration.displayName} access to the snapshots behind these results and registers the query in ${selectedIntegration.displayName}’s as a view. This lets you reuse the query or run new analyses directly within ${selectedIntegration.displayName}.`}
          onCancel={() => setSelectedIntegrationId(undefined)}
          onConfirm={async () => {
            await new Promise((resolve) => setTimeout(resolve, 3000));
            setSelectedIntegrationId(undefined);
          }}
          buttonIconClass={'material-symbols-share-outline'}
          buttonTitle='Share Data'
          confirmSnackbarText='Data shared successfully'
        />
      )}
    </>
  );
};

const Logo = ({ alt, src }: { alt: string; src: StaticImageData }) => (
  <Image
    alt={alt}
    src={src}
    width={24}
    height={24}
    style={{ width: '24px', height: '24px' }}
  />
);
